// src/context/AuthContext.js

import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; // Correct import

// Debug: Verify jwtDecode import
console.log('Imported jwtDecode:', jwtDecode);
if (typeof jwtDecode !== 'function') {
  console.error('jwtDecode is not a function. Check the import statement and jwt-decode version.');
}

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Initialize as null
  const [discordUserId, setDiscordUserId] = useState('');
  const [userType, setUserType] = useState(''); // 'admin', 'subscribe', or 'user'
  const [loading, setLoading] = useState(true);
  const [jwtToken, setJwtToken] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [guilds, setGuilds] = useState([]);
  const [selectedGuild, setSelectedGuild] = useState(null);
  const [guildId, setGuildId] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [subscriptionRefreshed, setSubscriptionRefreshed] = useState(false);

  const [discordUsername, setDiscordUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isSubscriptionFlow, setIsSubscriptionFlow] = useState(false);
  const [threadId, setThreadId] = useState('');

  const isFetchingGuilds = useRef(false); // Ref to track if fetchGuilds is in progress

  /**
   * Logout Function
   */
  const logout = useCallback(async () => {
    console.log('Initiating logout...');
    try {
      // Optionally, send a logout request to your backend if needed
      // await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`);

      // Clear all user-related state and localStorage
      setUser(null);
      setJwtToken('');
      setAccessToken('');
      setRefreshToken('');
      setDiscordUserId('');
      setUserType('');
      setDiscordUsername('');
      setAvatarUrl('');
      setIsSubscriptionFlow(false);
      localStorage.removeItem('jwtToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('discordUserId');
      localStorage.removeItem('guildId');
      localStorage.removeItem('threadId');
      localStorage.removeItem('userType');
      localStorage.removeItem('user');
      localStorage.removeItem('discordUsername');
      localStorage.removeItem('avatarUrl');
      setGuilds([]);
      setSelectedGuild(null);
      setGuildId('');
    } catch (error) {
      console.error('Logout failed:', error.response ? error.response.data : error.message);
    }
  }, []);

  /**
   * Refresh Access Token
   */
  const refreshAccessToken = useCallback(async () => {
    console.log('Attempting to refresh access token...');
    if (!refreshToken) {
      console.error('No refresh token available');
      logout();
      return null;
    }

    try {
      console.log('Attempting to refresh access token...');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh-token`,
        { refreshToken }
      );
      const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
      console.log('Access token refreshed successfully.');
      setAccessToken(newAccessToken);
      setRefreshToken(newRefreshToken);
      localStorage.setItem('accessToken', newAccessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      console.log('New accessToken and refreshToken stored in localStorage.');
      return newAccessToken;
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      logout();
      return null;
    }
  }, [refreshToken, logout]);

  /**
   * Refresh User Subscription
   */
  const refreshUser = useCallback(async () => {
    console.log('Refreshing user subscription...');
    if (!guildId || !accessToken) {
      console.warn('Cannot refresh user data without a guild ID or access token.');
      return false; // Default to inactive
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/check-subscription?guildId=${guildId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        console.log('Subscription status refreshed successfully:', response.data);
        setUser((prevUser) => ({
          ...prevUser,
          subscription: {
            isActive: response.data.isSubscribed,
            subscriptionLevel: response.data.subscriptionLevel,
            subscriptionExpiresAt: response.data.subscriptionExpiresAt,
          },
        }));
	console.log('User subscription updated in context.');
        return response.data.isSubscribed; // Return the subscription status
      }
    } catch (error) {
      console.error('Error refreshing user subscription status:', error);
      if (error.response?.status === 401) {
	console.log('Access token expired or invalid. Attempting to refresh.');
        // Handle token refresh or logout
        await refreshAccessToken(); // Attempt to refresh token
      }
    }

    return false; // Default to inactive on error
  }, [guildId, accessToken, refreshAccessToken]);

  /**
   * Fetch Guilds Function with useCallback and Exponential Backoff
   */
  const fetchGuilds = useCallback(async () => {
    if (isFetchingGuilds.current) {
      console.log('fetchGuilds is already in progress.');
      return;
    }

    isFetchingGuilds.current = true;

    console.log('fetchGuilds called with accessToken:', accessToken);
    console.log('fetchGuilds called with userType:', userType);
    console.log('Fetching guilds with accessToken:', accessToken, 'and userType:', userType);


    if (accessToken && (userType === 'admin' || userType === 'subscribe')) {
      console.log(`Fetching guilds for userType: ${userType}`);
      const maxRetries = 5;
      let attempt = 0;
      let waitTime = 1000; // Start with 1 second

      while (attempt < maxRetries) {
        try {
          console.log('Fetching guilds from backend...');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          setGuilds(response.data);
          console.log('Received filtered guilds:', response.data);

          // Store guilds in localStorage
          localStorage.setItem('guilds', JSON.stringify(response.data));

          // Optionally, set a default guild if not selected
          if (!selectedGuild && response.data.length > 0) {
            const defaultGuild = response.data[0];
            setSelectedGuild(defaultGuild.id);
            setGuildId(defaultGuild.id);
            console.log(`Default guild selected: ${defaultGuild.id}`);
          }

          break; // Exit loop on success
        } catch (error) {
          if (error.response && error.response.status === 401) {
            console.log('Access token expired or invalid, attempting to refresh.');
            const newAccessToken = await refreshAccessToken();
            if (newAccessToken) {
              // Retry fetching guilds with the new access token
              attempt += 1;
              waitTime *= 2; // Exponential backoff
              console.log(`Retrying fetchGuilds (${attempt}/${maxRetries}) after ${waitTime / 1000} seconds.`);
              await new Promise((res) => setTimeout(res, waitTime));
            } else {
              // Unable to refresh token, logout the user
              logout();
              break;
            }
          } else if (error.response && error.response.status === 429) {
            // Handle rate limiting
            const retryAfter = error.response.headers['retry-after'];
            waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : waitTime * 2; // Use 'Retry-After' if provided
            console.log(`Rate limited. Retrying fetchGuilds (${attempt + 1}/${maxRetries}) after ${waitTime / 1000} seconds.`);
            attempt += 1;
            await new Promise((res) => setTimeout(res, waitTime));
          } else {
            console.error('Error fetching guilds:', error);
            setGuilds([]);
            break; // Exit loop on non-rate-limit errors
          }

          if (attempt >= maxRetries) {
            console.error('Max retries reached. Could not fetch guilds.');
            setGuilds([]);
            break;
          }
        }
      }
    } else {
      console.warn('fetchGuilds: Not an admin or subscribe userType');
    }

    isFetchingGuilds.current = false;
    console.log('fetchGuilds process completed.');
  }, [
    accessToken,
    userType,
    selectedGuild,
    setGuilds,
    setSelectedGuild,
    setGuildId,
    refreshAccessToken,
    logout,
  ]);

  /**
   * Restore Authentication on Mount
   */
  useEffect(() => {
    const restoreAuth = async () => {
      console.log('Starting AuthContext restoration');
      const storedUserType = localStorage.getItem('userType');
      console.log('Stored User Type:', storedUserType);
      if (storedUserType) {
        setUserType(storedUserType);
	console.log('UserType set to:', storedUserType);
      }

      const storedJwtToken = localStorage.getItem('jwtToken');
      console.log('Stored JWT Token:', storedJwtToken);
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedDiscordUserId = localStorage.getItem('discordUserId');
      const storedGuildId = localStorage.getItem('guildId');
      const storedUser = localStorage.getItem('user');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedDiscordUsername = localStorage.getItem('discordUsername');
      const storedAvatarUrl = localStorage.getItem('avatarUrl');

      if (storedUserType === 'admin' || storedUserType === 'subscribe') {
	console.log('Restoring admin/subscribe userType...');
        if (storedJwtToken) {
	  console.log('Setting JWT Token:', storedJwtToken);
          setJwtToken(storedJwtToken);
          try {
	    console.log('Decoding JWT Token...');
            const decoded = jwtDecode(storedJwtToken);
	    console.log('Decoded JWT:', decoded);
            setUser({
              discordUserId: decoded.discordUserId,
              username: decoded.username,
              discriminator: decoded.discriminator,
              avatar: decoded.avatar,
              email: decoded.email,
              state: decoded.state,
            });
            console.log('Decoded JWT token successfully');
          } catch (error) {
            console.error('Error decoding JWT:', error);
            setUser(null);
          }
        }

        if (storedAccessToken) {
	  console.log('Setting Access Token:', storedAccessToken);
          setAccessToken(storedAccessToken);
          console.log('Restored accessToken:', storedAccessToken);
        }

        if (storedDiscordUserId) {
	  console.log('Setting Discord User ID:', storedDiscordUserId);
          setDiscordUserId(storedDiscordUserId);
          console.log('Restored discordUserId:', storedDiscordUserId);
        }

        if (storedRefreshToken) {
          console.log('Setting Refresh Token.');
          setRefreshToken(storedRefreshToken);
          console.log('Restored refreshToken');
        }
        console.log('Restored admin/subscribe flow successfully.');
      } else if (storedUserType === 'user') {
	console.log('Restoring user userType...');
        // Handle 'user' flow
        if (storedAccessToken) {
	  console.log('Setting Access Token:', storedAccessToken);
          setAccessToken(storedAccessToken);
          console.log('Restored accessToken:', storedAccessToken);
        }

        if (storedDiscordUserId) {
	  console.log('Setting Discord User ID:', storedDiscordUserId);
          setDiscordUserId(storedDiscordUserId);
          console.log('Restored discordUserId:', storedDiscordUserId);
        }

        if (storedDiscordUsername && storedAvatarUrl) {
	  console.log('Setting Discord Username and Avatar URL.');
          setUser({
            discordUserId: storedDiscordUserId,
            username: storedDiscordUsername,
            avatar: storedAvatarUrl,
          });
	  console.log('User set in context:', storedDiscordUsername);
        }

        if (storedGuildId) {
	  console.log('Setting Guild ID:', storedGuildId);
          setGuildId(storedGuildId);
          console.log('Restored guildId:', storedGuildId);
        }
	console.log('User restoration complete.');
      } else if (storedUser) {
	console.log('Restoring user from localStorage without userType...');
        // Fallback for cases where 'userType' is not set but 'user' exists
        setUser(JSON.parse(storedUser));
	console.log('User set in context:', JSON.parse(storedUser).username);
        console.log('Restored user from localStorage');

        if (storedAccessToken) {
	  console.log('Setting Access Token:', storedAccessToken);
          setAccessToken(storedAccessToken);
          console.log('Restored accessToken:', storedAccessToken);
        }

        if (storedDiscordUserId) {
          console.log('Setting Discord User ID:', storedDiscordUserId);
          setDiscordUserId(storedDiscordUserId);
          console.log('Restored discordUserId:', storedDiscordUserId);
        }

        if (storedGuildId) {
	  console.log('Setting Guild ID:', storedGuildId);
          setGuildId(storedGuildId);
          console.log('Restored guildId:', storedGuildId);
        }
	  console.log('Restored fallback user data.');    
      }

      setLoading(false);
      console.log('AuthContext initialization complete');
    };

    restoreAuth();
  }, []);

  /**
   * Fetch Guilds when accessToken or userType changes
   */
  useEffect(() => {
    if (!loading) {
      console.log('Loading complete. Fetching guilds...');
      fetchGuilds();
    }
  }, [accessToken, userType, loading, fetchGuilds]);

  /**
   * Refresh subscription whenever guildId changes
   */
  useEffect(() => {
    if (guildId) {
      console.log('Guild ID changed. Refreshing user subscription...');
      refreshUser();
    }
  }, [guildId, refreshUser]);

useEffect(() => {
  console.log('AccessToken updated:', accessToken);
}, [accessToken]);

useEffect(() => {
  console.log('userType updated:', userType);
}, [userType]);


  /**
   * Login function: initiate OAuth2 for Admin or User
   */
  const login = (flow = 'admin') => {
    console.log(`Initiating login flow: ${flow}`);
    setUserType(flow);
    localStorage.setItem('userType', flow);

    const client_id = process.env.REACT_APP_DISCORD_CLIENT_ID;
    let redirect_uri;
    let scope;

    if (flow === 'admin' || flow === 'subscribe') {
      redirect_uri = process.env.REACT_APP_DISCORD_REDIRECT_URI_ADMIN;
      scope = 'identify guilds';
    } else if (flow === 'user') {
      redirect_uri = process.env.REACT_APP_DISCORD_REDIRECT_URI_USER;
      scope = 'identify';
    } else {
      console.error(`Unsupported flow: ${flow}`);
      return;
    }

    if (!redirect_uri) {
      console.error(`REACT_APP_DISCORD_REDIRECT_URI_${flow.toUpperCase()} is not defined in the environment variables.`);
      return;
    }

    const discordAuthURL = `https://discord.com/api/oauth2/authorize?client_id=${client_id}&redirect_uri=${encodeURIComponent(
      redirect_uri
    )}&response_type=code&scope=${encodeURIComponent(scope)}&state=${flow}`;

    console.log('Redirecting to Discord OAuth2 URL:', discordAuthURL);
    window.location.href = discordAuthURL;
  };

  /**
   * Logout function is already defined above
   */

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        discordUserId,
        setDiscordUserId,
        userType,
        setUserType,
        loading,
        jwtToken,
        setJwtToken,
        accessToken,
        setAccessToken,
        refreshToken,
        setRefreshToken,
        login,
        logout,
        guilds,
        setGuilds,
        selectedGuild,
        setSelectedGuild,
        guildId,
        setGuildId,
        refreshUser,
        fetchGuilds, // **Ensure fetchGuilds is included in the context value**
	discordUsername,
	setDiscordUsername,
	avatarUrl,
	setAvatarUrl,
	isSubscriptionFlow,
	setIsSubscriptionFlow,
	threadId,
	setThreadId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; // Default export


